/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { NodeBaselineChangeDeclineReasons } from '@app/core/types';
import { isCluster2x } from '@shared/domain/cluster';
import { ClusterInfo } from '@shared/types/cluster';
import { ClusterNode, NodeBulkSelector, NodeConsistentId } from '@shared/types/node';

export const cantChangeNodeBaselineStatus = (
  node: ClusterNode,
  clusterInfo: ClusterInfo,
): false | NodeBaselineChangeDeclineReasons => {
  if (!isCluster2x(clusterInfo)) return false;
  // Cluster features should have higher priority
  if (!clusterInfo.info.active) return NodeBaselineChangeDeclineReasons.CLUSTER_IS_INACTIVE;
  if (!clusterInfo.info.configuration.persistenceEnabled) return NodeBaselineChangeDeclineReasons.CLUSTER_IS_IN_MEMORY;
  if (
    clusterInfo.info.configuration.baselineParameters &&
    clusterInfo.info.configuration.baselineParameters.autoAdjustEnabled
  )
    return NodeBaselineChangeDeclineReasons.AUTO_ADJUST_ENABLED;
  if (node.online && node.baselineNode) return NodeBaselineChangeDeclineReasons.NODE_IS_ONLINE;
  return false;
};

export const getNodeVersion = (node: ClusterNode): string => node.attributes['org.apache.ignite.build.ver'];

export const nodeHasAgent = ({ online, attributes }: ClusterNode) =>
  online && !!attributes['org.apache.ignite.features'] // https://ggsystems.atlassian.net/browse/GG-33460
    ? !!attributes['plugins.gridgain.control.center.agent.present']
    : true;
export const NO_AGENT_TOOLTIP = `This node doesn't have the agent on its classpath.`;

export const isBulkSelector = (selector: NodeConsistentId | NodeBulkSelector): selector is NodeBulkSelector =>
  [...Object.values<string>(NodeBulkSelector)].includes(selector);

export const getBulkSelectorName = (selector: string): string => {
  switch (selector) {
    case NodeBulkSelector.ALL:
      return 'All';
    case NodeBulkSelector.CLIENT:
      return 'Client';
    case NodeBulkSelector.SERVER:
      return 'Server';
    default:
      return 'N/A';
  }
};

export const isServerNode = (node: ClusterNode): boolean => !node.client;
